import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle2, XCircle, AlertCircle, Trash2 } from 'lucide-react';
import client from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import ApprovalChainTimeline from '../components/ApprovalChainTimeline';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle } from '../../styles/tokens';

const STATUS_CONFIG = {
  pending:   { label: 'Pendiente',  color: '#eab308', bg: '#fef9c3', icon: Clock },
  approved:  { label: 'Aprobada',   color: '#16a34a', bg: '#dcfce7', icon: CheckCircle2 },
  denied:    { label: 'Denegada',   color: '#dc2626', bg: '#fee2e2', icon: XCircle },
  cancelled: { label: 'Cancelada',  color: '#64748b', bg: '#f1f5f9', icon: AlertCircle },
};

const TYPE_LABELS = { vacation: 'Vacaciones', sick: 'Enfermedad', personal: 'Personal' };

export default function RequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [overlapping, setOverlapping] = useState([]);

  useEffect(() => {
    client.get(`/api/vacation/requests/${id}`)
      .then(r => {
        setRequest(r.data);
        // Fetch approved vacations that overlap with this request's date range
        if (r.data.start_date && r.data.end_date) {
          client.get('/api/vacation/calendar', {
            params: {
              month: new Date(r.data.start_date).getMonth() + 1,
              year: new Date(r.data.start_date).getFullYear(),
            }
          }).then(calRes => {
            const start = r.data.start_date.slice(0, 10);
            const end = r.data.end_date.slice(0, 10);
            const overlap = calRes.data.filter(v =>
              v.id !== parseInt(id) &&
              v.start_date.slice(0, 10) <= end &&
              v.end_date.slice(0, 10) >= start
            );
            setOverlapping(overlap);
          }).catch(() => {});
        }
      })
      .catch(err => { console.error(err); navigate('/vacaciones'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }

  async function handleAction(action) {
    setActionLoading(true);
    try {
      const res = await client.post(`/api/vacation/requests/${id}/${action}`, { comments: comments || null });
      setRequest(res.data);
      setComments('');
    } catch (err) {
      alert(err.response?.data?.error || `Error al ${action === 'approve' ? 'aprobar' : 'denegar'}.`);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCancel() {
    if (!confirm('¿Cancelar esta solicitud?')) return;
    setActionLoading(true);
    try {
      await client.delete(`/api/vacation/requests/${id}`);
      navigate('/vacaciones');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al cancelar.');
    } finally {
      setActionLoading(false);
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;
  if (!request) return null;

  const cfg = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  const isOwner = request.user_id === user?.id;
  const canCancel = isOwner && (request.status === 'pending' || request.status === 'approved');

  // Check if the current user is the pending approver at the current level
  const chain = request.approval_chain || [];
  const pendingRecord = chain.find(r => r.approver_id === user?.id && r.status === 'pending');
  const canReview = !!pendingRecord && request.status === 'pending';

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 640, margin: '0 auto' }}>
      <button
        onClick={() => navigate(-1)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 13, marginBottom: 12, fontFamily: 'Inter, sans-serif' }}
      >
        <ArrowLeft size={16} /> Volver
      </button>

      {/* Header card */}
      <div style={{ ...bubbleStyle, padding: '20px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10,
            background: cfg.bg, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={22} color={cfg.color} />
          </div>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: NAVY, margin: 0 }}>
              {TYPE_LABELS[request.type] || request.type}
            </h2>
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 10px',
              borderRadius: 10, background: cfg.bg, color: cfg.color,
            }}>
              {cfg.label}
              {request.current_level && request.status === 'pending' && (
                <span> · Nivel {request.current_level}</span>
              )}
            </span>
          </div>
        </div>

        {/* Details grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Empleado</div>
            <div style={{ color: NAVY, fontWeight: 500 }}>{request.user_name}</div>
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Días</div>
            <div style={{ color: NAVY, fontWeight: 500 }}>{parseFloat(request.days_requested)} día{parseFloat(request.days_requested) !== 1 ? 's' : ''} hábil{parseFloat(request.days_requested) !== 1 ? 'es' : ''}</div>
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Fecha inicio</div>
            <div style={{ color: NAVY, fontWeight: 500 }}>{formatDate(request.start_date)}</div>
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Fecha fin</div>
            <div style={{ color: NAVY, fontWeight: 500 }}>{formatDate(request.end_date)}</div>
          </div>
        </div>

        {request.reason && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: `1px solid ${BORDER_COLOR}` }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 2 }}>Motivo</div>
            <div style={{ fontSize: 13, color: NAVY }}>{request.reason}</div>
          </div>
        )}
      </div>

      {/* Approval Chain Timeline */}
      {chain.length > 0 && (
        <div style={{ ...bubbleStyle, padding: '16px 20px', marginBottom: 16 }}>
          <ApprovalChainTimeline chain={chain} />
        </div>
      )}

      {/* Overlapping approved vacations (visible to reviewers only, not the requesting employee) */}
      {user?.id !== request.user_id && overlapping.length > 0 && (
        <div style={{ ...bubbleStyle, padding: '14px 18px', marginBottom: 16, borderLeft: '3px solid #f59e0b' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
            Vacaciones aprobadas en este período ({overlapping.length})
          </div>
          {overlapping.map(v => (
            <div key={v.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
              fontSize: 12, color: '#78350f',
            }}>
              <span style={{ fontWeight: 600 }}>{v.user_name}</span>
              <span style={{ color: '#94a3b8' }}>·</span>
              <span>{TYPE_LABELS[v.type] || v.type}</span>
              <span style={{ color: '#94a3b8' }}>·</span>
              <span>{formatDate(v.start_date)} — {formatDate(v.end_date)}</span>
              <span style={{ color: '#94a3b8' }}>·</span>
              <span>{parseFloat(v.days_requested)} día{parseFloat(v.days_requested) !== 1 ? 's' : ''}</span>
            </div>
          ))}
        </div>
      )}

      {/* Approver actions */}
      {canReview && (
        <div style={{ ...bubbleStyle, padding: '16px 20px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 4 }}>
            Acción — Nivel {pendingRecord.level}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
            Esta solicitud requiere su aprobación.
          </div>
          <textarea
            value={comments}
            onChange={e => setComments(e.target.value)}
            placeholder="Comentarios (opcional)"
            rows={2}
            style={{ ...fieldStyle, resize: 'vertical', marginBottom: 12 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => handleAction('approve')}
              disabled={actionLoading}
              style={{ ...navyBtn, flex: 1, background: '#16a34a', fontSize: 13 }}
            >
              Aprobar
            </button>
            <button
              onClick={() => handleAction('deny')}
              disabled={actionLoading}
              style={{ ...navyBtn, flex: 1, background: '#dc2626', fontSize: 13 }}
            >
              Denegar
            </button>
          </div>
        </div>
      )}

      {/* Cancel button */}
      {canCancel && (
        <button
          onClick={handleCancel}
          disabled={actionLoading}
          style={{
            width: '100%', padding: '10px 0', fontSize: 13, fontWeight: 600,
            background: 'transparent', color: '#dc2626',
            border: '1.5px solid #fecaca', borderRadius: 8,
            cursor: 'pointer', fontFamily: 'Inter, sans-serif',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <Trash2 size={14} /> Cancelar Solicitud
        </button>
      )}
    </div>
  );
}
